import React from "react";
import { calculateColumnWidths, ColumnWidthConfig } from "../columnWidthCalculator";

interface ColumnWidthState {
  featureWidth: number;
  dataWidth: number;
  visibleColumns: number;
  scrollableColumns: number;
  shouldScroll: boolean;
}

export const useColumnWidths = (
  tableRef: React.RefObject<HTMLDivElement>,
  tableContainerRef: React.RefObject<HTMLDivElement>,
  dataColumnCount: number
) => {
  const [columnWidthConfig, setColumnWidthConfig] = React.useState<ColumnWidthState>({
    featureWidth: 0,
    dataWidth: 0,
    visibleColumns: 0,
    scrollableColumns: 0,
    shouldScroll: false,
  });

  React.useEffect(() => {
    const updateColumnWidths = (): ColumnWidthConfig | null => {
      if (!tableRef.current || !tableContainerRef.current) return null;

      const containerWidth = tableContainerRef.current.clientWidth;

      if (dataColumnCount === 0) {
        const defaultConfig: ColumnWidthConfig = {
          featureColumnPixels: containerWidth,
          dataColumnPixels: 0,
          dataColumnMinPixels: 0,
          visibleColumns: 0,
          scrollableColumns: 0,
          shouldScroll: false,
          totalTableWidth: containerWidth,
        };

        setColumnWidthConfig({
          featureWidth: containerWidth,
          dataWidth: 0,
          visibleColumns: 0,
          scrollableColumns: 0,
          shouldScroll: false,
        });

        tableRef.current.style.setProperty(
          "--feature-column-pixels",
          `${containerWidth}px`
        );
        tableRef.current.style.setProperty("--data-column-pixels", "0px");
        tableRef.current.style.setProperty("--data-column-min-pixels", "0px");
        tableRef.current.style.removeProperty("--data-column-max-pixels");
        tableRef.current.style.removeProperty("--table-total-width");
        tableRef.current.setAttribute("data-mode", "fill");
        tableRef.current.style.removeProperty("width");

        return defaultConfig;
      }

      const widthConfig = calculateColumnWidths(containerWidth, dataColumnCount);

      setColumnWidthConfig({
        featureWidth: widthConfig.featureColumnPixels,
        dataWidth: widthConfig.dataColumnPixels,
        visibleColumns: widthConfig.visibleColumns,
        scrollableColumns: widthConfig.scrollableColumns,
        shouldScroll: widthConfig.shouldScroll,
      });

      tableRef.current.style.setProperty(
        "--feature-column-pixels",
        `${widthConfig.featureColumnPixels}px`
      );
      tableRef.current.style.setProperty(
        "--data-column-pixels",
        `${widthConfig.dataColumnPixels}px`
      );
      tableRef.current.style.setProperty(
        "--data-column-min-pixels",
        `${widthConfig.dataColumnMinPixels}px`
      );
      tableRef.current.style.setProperty(
        "--table-total-width",
        `${widthConfig.totalTableWidth}px`
      );

      if (widthConfig.dataColumnMaxPixels) {
        tableRef.current.style.setProperty(
          "--data-column-max-pixels",
          `${widthConfig.dataColumnMaxPixels}px`
        );
      } else {
        tableRef.current.style.removeProperty("--data-column-max-pixels");
      }

      if (widthConfig.shouldScroll) {
        tableRef.current.setAttribute("data-mode", "scroll");
        tableRef.current.style.width = `${widthConfig.totalTableWidth}px`;
      } else {
        tableRef.current.setAttribute("data-mode", "fill");
        tableRef.current.style.removeProperty("width");
        tableRef.current.style.removeProperty("--table-total-width");
      }

      tableRef.current.style.setProperty(
        "--total-columns",
        `${dataColumnCount + 1}`
      );
      tableRef.current.style.setProperty(
        "--data-columns",
        `${dataColumnCount}`
      );

      return widthConfig;
    };

    const widthConfig = updateColumnWidths();

    if (!widthConfig?.shouldScroll && tableContainerRef.current) {
      tableContainerRef.current.scrollLeft = 0;
    }

    let resizeAnimationFrame: number | null = null;
    let scrollDispatchFrame: number | null = null;

    const handleResize = () => {
      if (resizeAnimationFrame) {
        cancelAnimationFrame(resizeAnimationFrame);
      }

      resizeAnimationFrame = requestAnimationFrame(() => {
        const newWidthConfig = updateColumnWidths();
        if (!newWidthConfig?.shouldScroll && tableContainerRef.current) {
          tableContainerRef.current.scrollLeft = 0;
        }
        // Force scroll state recalculation after resize
        if (scrollDispatchFrame) {
          cancelAnimationFrame(scrollDispatchFrame);
        }
        scrollDispatchFrame = requestAnimationFrame(() => {
          if (tableContainerRef.current) {
            tableContainerRef.current.dispatchEvent(new Event('scroll'));
          }
          scrollDispatchFrame = null;
        });
        resizeAnimationFrame = null;
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeAnimationFrame) {
        cancelAnimationFrame(resizeAnimationFrame);
        resizeAnimationFrame = null;
      }
      if (scrollDispatchFrame) {
        cancelAnimationFrame(scrollDispatchFrame);
        scrollDispatchFrame = null;
      }
    };
  }, [dataColumnCount, tableRef, tableContainerRef]);

  return columnWidthConfig;
};